import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Box, HStack } from '@chakra-ui/react';

import { TransactionStatus, TransactionType } from '__generated__/GQL';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import { BankAccountIllustrationName } from 'components/BankAccountIcon';
import { StackedBankAccountIcons } from 'components/StackedBankAccountIcons';
import { logEvent } from 'features/Analytics/analytics';
import TransactionAmount from 'features/Bank/components/TransactionAmount';
import TransactionIcon from 'features/Bank/pages/Overview/Transactions/TransactionIcon';
import { formatRelative } from 'helpers/date';
import { capitalizeFirstLetter } from 'helpers/string';

import locales from '../../../../locales';
import { useBankOverviewSearchParams } from '../../../hooks/useBankOverviewSearchParams';
import useTransactionInitiatorName from '../../../hooks/useTransactionInitiatorName';
import { TransactionLabelBuilder } from '../../../utils';
import { TransactionNode } from '../types';
import TransactionOldIbanBadge from './components/TransactionOldIbanBadge';

interface TransactionItemProps {
  transaction: TransactionNode;
  bankAccountIllustrations: Record<string, BankAccountIllustrationName>;
}

const TransactionItem: FC<TransactionItemProps> = ({
  bankAccountIllustrations,
  transaction,
}) => {
  const { getTransactionPath, transactionId: selectedTransactionId } =
    useBankOverviewSearchParams();

  const { shouldDisplayInitiatorName } =
    useTransactionInitiatorName(transaction);

  const {
    bankAccountId,
    hasReceipts,
    initiatorName,
    isPersonal,
    paymentMethod,
    status,
    transactionAt,
    transactionId,
    type,
  } = transaction;

  /**
   * @todo Remove after users have been properly notified of the feature
   * @see https://linear.app/shine/issue/CAC-24
   */
  const onClick = () => {
    if (type === TransactionType.Payin) {
      logEvent({
        name: 'Payin Transaction Clicked',
        properties: {
          transactionId,
        },
      });
    }
  };

  const label = TransactionLabelBuilder.fromTransaction(transaction).build();

  const isActive = transactionId === selectedTransactionId;

  return (
    <SunshineCard
      _hover={{
        backgroundColor: 'grey.200',
      }}
      alignItems="center"
      as={Link}
      border="1.5px solid"
      borderColor={isActive ? 'card-borderColor-selected' : 'transparent'}
      isActive={isActive}
      isBoxed={false}
      onClick={onClick}
      padding="space-16"
      to={getTransactionPath(transactionId)}
    >
      <SunshineCard.Slot name="aside">
        {'toBankAccountId' in transaction ? (
          <StackedBankAccountIcons
            bottomIllustrationName={
              bankAccountIllustrations[
                transaction.toBankAccountId as string
              ] as BankAccountIllustrationName
            }
            inverted
            size="big"
            topIllustrationName={bankAccountIllustrations[bankAccountId]}
          />
        ) : (
          <TransactionIcon size="md" transaction={transaction} />
        )}
      </SunshineCard.Slot>

      <SunshineCard.Content
        alignItems="center"
        display="grid"
        gap="space-16"
        gridTemplateColumns="1fr minmax(100px, auto) minmax(130px, auto) minmax(20px, auto)"
        paddingY="0"
      >
        <Box flexGrow={1}>
          <Text
            noOfLines={1}
            variant={status === TransactionStatus.Pending ? 'light' : 'primary'}
          >
            {label}
          </Text>

          <HStack alignItems="flex-start">
            <Text size="sm" variant="light">
              {capitalizeFirstLetter(
                formatRelative(new Date(transactionAt), new Date()),
              )}
              {shouldDisplayInitiatorName ? ` · ${initiatorName}` : null}
            </Text>

            {isPersonal ? (
              <SunshineIcon color="grey.600" name="personal" size="icon-16" />
            ) : null}

            <TransactionOldIbanBadge transaction={transaction} />
          </HStack>
        </Box>

        <Text textAlign="right" variant="light">
          {locales[paymentMethod]}
        </Text>

        <TransactionAmount transaction={transaction} />

        {hasReceipts ? (
          <SunshineIcon color="grey.600" name="paper-clip" size="icon-20" />
        ) : null}
      </SunshineCard.Content>
    </SunshineCard>
  );
};

export default TransactionItem;
